import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import axios from 'axios';

interface BetDetail {
  betId: number;
  betNumber: string;
  betAmount: number;
  operatorName: string;
  gameType: string;
  isWin: boolean;
  winningTier: string;
  winAmount: number;
  placedAt: string;
  userTakePercentage: number;
  salesCommissionRate: number;
  salesCommissionAmount: number;
  hierarchicalAmount: number;
}

interface UserDetailReport {
  userId: number;
  username: string;
  fullName: string;
  reportDate: string;
  ownBetsCount: number;
  ownBetsAmount: number;
  ownWins: number;
  ownLosses: number;
  ownWinAmount: number;
  ownBets: BetDetail[];
  downlineDetails: any[];
  totalSalesCommission: number;
  totalHierarchicalEarned: number;
  totalHierarchicalOwed: number;
  netPosition: number;
}

const UserSettlementDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dateParam = searchParams.get('date');
  
  const [userDetail, setUserDetail] = useState<UserDetailReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ownBetsTab, setOwnBetsTab] = useState(0);

  useEffect(() => {
    if (userId && dateParam) {
      fetchUserDetail();
    } else {
      setError('Missing userId or date parameter');
      setLoading(false);
    }
  }, [userId, dateParam]);

  const fetchUserDetail = async () => {
    if (!userId || !dateParam) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`/settlement/admin-user-detail/${userId}/${dateParam}`);
      
      // Handle both camelCase and PascalCase property names
      const userData = response.data;
      if (userData.OwnBets && !userData.ownBets) {
        userData.ownBets = userData.OwnBets;
      }
      
      setUserDetail(userData);
      setOwnBetsTab(0);
    } catch (err: any) {
      console.error('Error fetching user details:', err);
      setError(err.response?.data?.message || 'Failed to fetch user details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!userDetail) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Alert severity="warning">No user data available</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
        <Typography variant="h4">
          Settlement Report - {userDetail.fullName || userDetail.username}
        </Typography>
      </Box>

      {/* User Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Username
              </Typography>
              <Typography variant="h6">{userDetail.username}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Full Name
              </Typography>
              <Typography variant="h6">{userDetail.fullName || 'N/A'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Report Date
              </Typography>
              <Typography variant="h6">
                {new Date(userDetail.reportDate).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Net Position
              </Typography>
              <Typography 
                variant="h6" 
                color={userDetail.netPosition >= 0 ? 'success.main' : 'error.main'}
              >
                {formatCurrency(userDetail.netPosition)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Own Betting Activity Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Own Betting Activity
          </Typography>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Total Bets
              </Typography>
              <Typography variant="h6">
                {userDetail.ownBetsCount}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Total Amount
              </Typography>
              <Typography variant="h6">
                {formatCurrency(userDetail.ownBetsAmount)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Wins
              </Typography>
              <Typography variant="h6" color="success.main">
                {userDetail.ownWins}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Win Amount
              </Typography>
              <Typography variant="h6" color="success.main">
                {formatCurrency(userDetail.ownWinAmount)}
              </Typography>
            </Grid>
          </Grid>

          {/* Bet Details with Tabs */}
          {userDetail.ownBets && Array.isArray(userDetail.ownBets) && userDetail.ownBets.length > 0 ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={ownBetsTab} onChange={(e, newValue) => setOwnBetsTab(newValue)}>
                  <Tab label={`Winning Bets (${userDetail.ownBets.filter(b => b.isWin).length})`} />
                  <Tab label={`Losing Bets (${userDetail.ownBets.filter(b => !b.isWin).length})`} />
                </Tabs>
              </Box>

              {/* Winning Bets Tab */}
              {ownBetsTab === 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Bet Number</TableCell>
                        <TableCell>Operator</TableCell>
                        <TableCell>Game Type</TableCell>
                        <TableCell align="right">Bet Amount</TableCell>
                        <TableCell>Winning Tier</TableCell>
                        <TableCell align="right">Win Amount</TableCell>
                        <TableCell align="right">Sales Commission</TableCell>
                        <TableCell align="right">Hierarchical Commission</TableCell>
                        <TableCell>Placed At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userDetail.ownBets
                        .filter(bet => bet.isWin)
                        .map((bet) => (
                          <TableRow 
                            key={bet.betId} 
                            sx={{ 
                              backgroundColor: (theme) => theme.palette.mode === 'light' 
                                ? 'rgba(76, 175, 80, 0.08)' 
                                : 'rgba(76, 175, 80, 0.15)',
                              '&:hover': {
                                backgroundColor: (theme) => theme.palette.mode === 'light'
                                  ? 'rgba(76, 175, 80, 0.12)'
                                  : 'rgba(76, 175, 80, 0.2)'
                              }
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold" color="success.main">
                                {bet.betNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {bet.operatorName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {bet.gameType}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">
                                {formatCurrency(bet.betAmount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={bet.winningTier} color="success" size="small" />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="success.main" fontWeight="bold">
                                {formatCurrency(bet.winAmount)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="success.main">
                                {formatCurrency(bet.salesCommissionAmount)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                color={bet.hierarchicalAmount > 0 ? 'success.main' : 'error.main'}
                              >
                                {bet.hierarchicalAmount > 0 ? '+' : ''}
                                {formatCurrency(bet.hierarchicalAmount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {new Date(bet.placedAt).toLocaleString()}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      {userDetail.ownBets.filter(bet => bet.isWin).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No winning bets
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Losing Bets Tab */}
              {ownBetsTab === 1 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Bet Number</TableCell>
                        <TableCell>Operator</TableCell>
                        <TableCell>Game Type</TableCell>
                        <TableCell align="right">Bet Amount</TableCell>
                        <TableCell align="right">Sales Commission</TableCell>
                        <TableCell align="right">Hierarchical Commission</TableCell>
                        <TableCell>Placed At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userDetail.ownBets
                        .filter(bet => !bet.isWin)
                        .map((bet) => (
                          <TableRow key={bet.betId}>
                            <TableCell>{bet.betNumber}</TableCell>
                            <TableCell>{bet.operatorName}</TableCell>
                            <TableCell>{bet.gameType}</TableCell>
                            <TableCell align="right">{formatCurrency(bet.betAmount)}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="success.main">
                                {formatCurrency(bet.salesCommissionAmount)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                color={bet.hierarchicalAmount > 0 ? 'success.main' : 'error.main'}
                              >
                                {bet.hierarchicalAmount > 0 ? '+' : ''}
                                {formatCurrency(bet.hierarchicalAmount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {new Date(bet.placedAt).toLocaleString()}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      {userDetail.ownBets.filter(bet => !bet.isWin).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No losing bets
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {userDetail.ownBetsCount > 0 
                  ? 'Bet details are not available. Please regenerate the settlement report for this date to view bet details.'
                  : 'No bets placed on this date.'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Commission Summary */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Commission Summary
          </Typography>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Total Sales Commission
              </Typography>
              <Typography variant="h6" color="success.main">
                {formatCurrency(userDetail.totalSalesCommission)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Hierarchical Earned
              </Typography>
              <Typography variant="h6" color="success.main">
                {formatCurrency(userDetail.totalHierarchicalEarned)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Hierarchical Owed
              </Typography>
              <Typography variant="h6" color="error.main">
                {formatCurrency(userDetail.totalHierarchicalOwed)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserSettlementDetail;

